// ============================================================
// ExpressSwaggerDocs.jsx
//
// A branded, responsive course content page for the
// "API Documentation with Swagger" lesson.
//
// Author: ProDev Architecture Series (Documentation Branch)
// Features: OpenAPI 3.0 configuration, JSDoc integration,
//           and navigation bridge to the Dashboard.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import MenuBookIcon          from "@mui/icons-material/MenuBook";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import TerminalIcon          from "@mui/icons-material/Terminal";
import LightbulbIcon         from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon       from "@mui/icons-material/ContentCopy";
import DoneIcon              from "@mui/icons-material/Done";
import SchoolIcon            from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon         from "@mui/icons-material/ArrowBack";
import CodeIcon              from "@mui/icons-material/Code";
import AutoFixHighIcon        from "@mui/icons-material/AutoFixHigh";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.2 }}>// {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText, whiteSpace: "pre-wrap" }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function ExpressSwaggerDocs() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (!showScroll && window.pageYOffset > 400) setShowScroll(true);
            else if (showScroll && window.pageYOffset <= 400) setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, [showScroll]);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Documentation · Module 27"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Swagger <span>API Documentation</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Automatic, interactive documentation is a hallmark of professional APIs. Swagger (OpenAPI) allows developers to explore, test, and integrate with your endpoints directly from a live browser interface.
                    </Typography>
                </Box>

                {/* 1. INSTALLATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="1. Core Dependencies" />
                    <CodeBlock lines={[
                        { cmd: "npm install swagger-ui-express swagger-jsdoc", comment: "Install the UI and JSDoc parser" },
                    ]} />
                </Box>

                {/* 2. CONFIGURATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<CodeIcon />} label="2. Defining Swagger Options" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Create a centralized configuration file to define your API's metadata and where to find the JSDoc annotations.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "// docs/swaggerOptions.js", comment: "" },
                        { cmd: "module.exports = {", comment: "" },
                        { cmd: "  definition: {", comment: "" },
                        { cmd: "    openapi: '3.0.0',", comment: "Industry standard version" },
                        { cmd: "    info: { title: 'ProDev API', version: '1.0.0' },", comment: "" },
                        { cmd: "    servers: [{ url: 'http://localhost:4000' }]", comment: "" },
                        { cmd: "  },", comment: "" },
                        { cmd: "  apis: ['./routes/*.js']", comment: "Scanner path" },
                        { cmd: "};", comment: "" },
                    ]} />
                </Box>

                {/* 3. IMPLEMENTATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<IntegrationInstructionsIcon />} label="3. Serving the UI" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Mount the Swagger specs to a route like <code style={{color: BRAND.white}}>/api-docs</code> to render the interactive dashboard.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "const swaggerUi = require('swagger-ui-express');", comment: "" },
                        { cmd: "const swaggerJsdoc = require('swagger-jsdoc');", comment: "" },
                        { cmd: "const specs = swaggerJsdoc(require('./docs/swaggerOptions'));", comment: "" },
                        { cmd: "", comment: "" },
                        { cmd: "app.use('/api-docs', swaggerUi.serve, swaggerUi.setup(specs));", comment: "Interactive Dashboard" },
                    ]} />
                </Box>

                {/* 4. JSDOC ANNOTATIONS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<AutoFixHighIcon />} label="4. Living Documentation" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Write documentation directly above your route handlers. This ensures your docs stay updated as your code changes.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "/**", comment: "" },
                        { cmd: " * @swagger", comment: "Triggers Swagger scan" },
                        { cmd: " * /ping:", comment: "" },
                        { cmd: " * get:", comment: "" },
                        { cmd: " * summary: Health Check", comment: "" },
                        { cmd: " * responses: [200: Pong!]", comment: "" },
                        { cmd: " */", comment: "" },
                        { cmd: "app.get('/ping', (req, res) => res.send('pong'));", comment: "" },
                    ]} />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* BEST PRACTICES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<MenuBookIcon />} label="Documentation Best Practices" />
                    <Grid container spacing={2}>
                        {[
                            { title: "Grouping", desc: "Use @swagger tags to group endpoints (e.g., 'Auth', 'User Management').", icon: <StarIcon /> },
                            { title: "Version Control", desc: "Keep your OpenAPI spec versioned alongside your API to track breaking changes.", icon: <CodeIcon /> },
                            { title: "Examples", desc: "Always provide request body and response examples to reduce integration friction.", icon: <CheckCircleOutlineIcon /> },
                        ].map((item) => (
                            <Grid item xs={12} md={4} key={item.title}>
                                <Paper sx={{ p: 2.5, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, height: '100%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        {React.cloneElement(item.icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                                        <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14 }}>{item.title}</Typography>
                                    </Box>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{item.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/express-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "999px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Back to Express Dashboard
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Swagger UI allows you to "Try it out" by sending real requests. If your routes are protected by JWT, remember to configure the <strong>securityDefinitions</strong> in your swaggerOptions so you can paste your token directly into the UI.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
